import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Form } from '@core/interfaces';
import { endOfDay, mapIds, mapStringToArrayOfIds, startOfDay } from '@core/utils/filters-utils';
import { DateIntervalValidation } from '@validators/date-interval-validation';
import { DateValidation } from '@validators/date.validation';
import { isEqual } from 'lodash';
import moment from 'moment';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { ReportsService } from '../../reports.service';

@Injectable({
  providedIn: 'root'
})
export class FilterService {
  private routePage = 'relatorios/formularios';

  public selectedForm: Form;
  public questionsCount: number;

  private readonly removeSubscriptions = new Subject<void>();

  public defaultFilterValue = {
    initialDate: moment().subtract(1, 'month'),
    finalDate: moment(),
    locations: null,
    maintenanceTypes: null,
    segments: null,
    companies: null,
    locationGroups: null
  };

  public isFilterOn = false;
  public isFilterExpanded = false;

  public filterForm = new FormGroup({
    initialDate: new FormControl(this.defaultFilterValue.initialDate, [Validators.required, DateValidation.validate]),
    finalDate: new FormControl(this.defaultFilterValue.finalDate, [Validators.required, DateValidation.validate]),
    locations: new FormControl(this.defaultFilterValue.locations),
    maintenanceTypes: new FormControl(this.defaultFilterValue.maintenanceTypes),
    segments: new FormControl(this.defaultFilterValue.segments),
    companies: new FormControl(this.defaultFilterValue.companies),
    locationGroups: new FormControl(this.defaultFilterValue.locationGroups)
  },
    {
      validators: [
        DateIntervalValidation.validate({
          from: 'initialDate',
          to: 'finalDate',
          maxDiffLte: 1,
          unit: 'year'
        })
      ]
    });

  constructor(
    private readonly reportsService: ReportsService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {
    this.reportsService.preserveSavedFilterValues(
      'forms-dashboard-filter',
      this.filterForm,
      this.defaultFilterValue
    );
    this.filterForm.valueChanges.pipe(
      takeUntil(this.removeSubscriptions),
      filter(() => this.filterForm.valid),
    ).subscribe(() => {
      this.isFilterOn = !isEqual({
        initialDate: moment(this.filterForm.value.initialDate).format('LL'),
        finalDate: moment(this.filterForm.value).format('LL'),
        locations: this.filterForm.value.locations,
        maintenanceTypes: this.filterForm.value.maintenanceTypes,
        segments: this.filterForm.value.segments,
        companies: this.filterForm.value.companies,
        locationGroups: this.filterForm.value.locationGroups
      }, {
        initialDate: moment(this.defaultFilterValue.initialDate).format('LL'),
        finalDate: moment(this.defaultFilterValue.finalDate).format('LL'),
        locations: this.defaultFilterValue.locations,
        maintenanceTypes: this.defaultFilterValue.maintenanceTypes,
        segments: this.defaultFilterValue.segments,
        companies: this.defaultFilterValue.companies,
        locationGroups: this.defaultFilterValue.locationGroups
      });
      if (this.isFilterOn) {
        const where = this.toDetailQuery();
        this.setUrlAfterFilter(where);
      }
    });
  }

  get valueChanges(): Observable<any> {
    return this.filterForm.valueChanges;
  }

  get value(): any {
    return this.filterForm.value;
  }

  reload(): void {
    this.filterForm.updateValueAndValidity();
  }

  toggleExpanded(): void {
    this.isFilterExpanded = !this.isFilterExpanded;
  }

  clearFilters(): void {
    const queryParams = this.route.snapshot.queryParams;
    let origin = location.origin + location.pathname;

    if (queryParams.formId_eq) {
      origin = origin.concat(`#/${this.routePage}/detalhes?formId_eq=`, queryParams.formId_eq);
    } else {
      origin = origin.concat(`#/${this.routePage}`);
    }

    localStorage.removeItem('forms-dashboard-filter');
    window.history.pushState(
      {},
      null,
      origin
    );
    this.filterForm.patchValue(this.defaultFilterValue);
  }

  public toQuery(value: any): any {
    return {
      locationId_in: mapIds(value.locations),
      segmentId_in: mapIds(value.segments),
      companyId_in: mapIds(value.companies),
      createdAt_lte: endOfDay(value.finalDate),
      createdAt_gte: startOfDay(value.initialDate),
      locationGroupId_in: mapIds(value.locationGroups),
      maintenanceTypeId_in: mapIds(value.maintenanceTypes)
    };
  }

  public toDetailQuery(): any {
    return {
      formId_eq: this.route.snapshot.queryParams.formId_eq,
      locationId_in: mapIds(this.value.locations),
      segmentId_in: mapIds(this.value.segments),
      companyId_in: mapIds(this.value.companies),
      createdAt_lte: endOfDay(this.value.finalDate),
      createdAt_gte: startOfDay(this.value.initialDate),
      locationGroupId_in: mapIds(this.value.locationGroups),
      maintenanceTypeId_in: mapIds(this.value.maintenanceTypes)
    };
  }

  private setUrlAfterFilter(where): void {
    const isOnTheDetailsPage = this.router.url.includes('detalhes');
    const routePage = `${this.routePage}${isOnTheDetailsPage ? '/detalhes' : ''}`;
    const queryParams = this.reportsService.keepOnlyChangedValuesOnQueryParams(where, {
      formId_eq: null,
      ...this.toQuery(this.defaultFilterValue),
    });
    const urlTree = this.router.createUrlTree([routePage], { queryParams });
    const url = this.router.serializeUrl(urlTree);
    const pathName = `${location.origin}${location.pathname}#${url}`;

    window.history.pushState({}, null, pathName);
  }

  public setFilterFromQueryParams(): void {
    const queryParams = this.route.snapshot.queryParams;
    if (isEqual(queryParams, {})) {
      return;
    }

    const filterValue = {
      initialDate: queryParams.createdAt_gte ? moment(queryParams.createdAt_gte) : this.defaultFilterValue.initialDate,
      finalDate: queryParams.createdAt_lte ? moment(queryParams.createdAt_lte) : this.defaultFilterValue.finalDate,
      locations: queryParams.locationId_in ? mapStringToArrayOfIds(queryParams.locationId_in) : this.defaultFilterValue.locations,
      maintenanceTypes: queryParams.maintenanceTypeId_in ? mapStringToArrayOfIds(queryParams.maintenanceTypeId_in) : this.defaultFilterValue.maintenanceTypes,
      segments: queryParams.segmentId_in ? mapStringToArrayOfIds(queryParams.segmentId_in) : this.defaultFilterValue.segments,
      companies: queryParams.companyId_in ? mapStringToArrayOfIds(queryParams.companyId_in) : this.defaultFilterValue.companies,
      locationGroups: queryParams.locationGroupId_in ? mapStringToArrayOfIds(queryParams.locationGroupId_in) : this.defaultFilterValue.locationGroups
    };

    this.filterForm.setValue(filterValue);
    this.filterForm.updateValueAndValidity();
    this.isFilterOn = true;
  }
}
